import { Armchair as Stool } from "lucide-react";
import type { ReactNode } from "react";
import { fr } from "@/i18n/fr";
import { SiteHeader } from "./SiteHeader";
import { Wordmark } from "./Wordmark";

interface AuthLayoutProps {
	title: string;
	lead: string;
	children: ReactNode;
}

/** Layout of the sign-in pages: a quote on ink, the form in a card beside it (below on phones). */
export function AuthLayout({ title, lead, children }: AuthLayoutProps) {
	return (
		<div className="page">
			<div className="md:grid md:min-h-full md:grid-cols-[1fr_1fr]">
				<aside className="relative bg-ink px-4 pt-28 pb-16 text-on-ink md:flex md:flex-col md:justify-end md:px-10 md:pb-12">
					<SiteHeader back="/" onInk />
					<div aria-hidden="true" className="hidden md:block">
						<Stool className="mb-10 size-40 opacity-80" strokeWidth={0.8} />
					</div>
					<blockquote className="m-0 max-w-[420px] font-display text-[28px] text-balance leading-8 md:text-[34px] md:leading-10">
						« Douze étapes, zéro pièce en trop. C’était presque reposant. »
					</blockquote>
					<cite className="mt-3 block text-[13px] text-on-ink/60 not-italic leading-[18px]">
						Camille, BEKVÄM monté un dimanche matin
					</cite>
				</aside>
				<main className="-mt-8 relative z-[1] mx-4 grid gap-5 rounded-xl bg-bg-elev p-5 shadow-lg md:m-0 md:w-full md:max-w-[460px] md:justify-self-center md:self-center md:rounded-none md:bg-transparent md:p-10 md:shadow-none">
					<Wordmark />
					<div className="grid gap-2">
						<h1 className="font-display text-[32px] leading-9 tracking-tight">
							{title}
						</h1>
						<p className="text-fg-muted">{lead}</p>
					</div>
					{children}
					<p className="text-center text-fg-muted text-xs">{fr.login.legal}</p>
				</main>
			</div>
		</div>
	);
}

/** An inline message of the sign-in pages. */
export function Notice({
	tone,
	children,
}: {
	tone: "ok" | "danger";
	children: ReactNode;
}) {
	return tone === "ok" ? (
		<output className="rounded-md bg-ok-soft px-3 py-2 text-[13px] text-ok leading-[18px]">
			{children}
		</output>
	) : (
		<p
			role="alert"
			className="rounded-md bg-danger-soft px-3 py-2 text-[13px] text-danger leading-[18px]"
		>
			{children}
		</p>
	);
}
